// Mirror of packages/db/src/activity-kinds.ts (spec 26 / ATL-112): the kind
// vocabulary, which kinds a user may switch off, and the per-source label
// table the settings panels print. The provider -> kind mapping itself is
// server-only. Keep the two files in step.

export const ACTIVITY_KINDS = ["hike", "walk", "run", "other"] as const;
export type ActivityKind = (typeof ACTIVITY_KINDS)[number];

export const EXCLUDABLE_KINDS: readonly ActivityKind[] = ["hike", "walk", "run"];

/**
 * duplicate (spec 37): one walk synced from two sources, this copy parked.
 * trip_route (spec 38): a GPX in use as a trip's planned route.
 * Both are archived (restore is local), like removed.
 */
export type ActivityExclusionReason = "removed" | "skipped_by_preference" | "duplicate" | "trip_route";

/** Plural, for chips and toggles. */
export const ACTIVITY_KIND_LABELS: Record<ActivityKind, string> = {
  hike: "Hikes",
  walk: "Walks",
  run: "Runs",
  other: "Other",
};

/** Singular, for row meta and confirm copy ("Remove 43 runs"). */
export const ACTIVITY_KIND_NOUN: Record<ActivityKind, { one: string; many: string }> = {
  hike: { one: "hike", many: "hikes" },
  walk: { one: "walk", many: "walks" },
  run: { one: "run", many: "runs" },
  other: { one: "activity", many: "activities" },
};

export type KindLabelSource = "strava" | "apple_health" | "health_connect" | "gpx";

/**
 * What each toggle covers, per source, in the provider's own words. The
 * settings panel prints the rows for the sources the user has connected so
 * they can see exactly which of their recorded types would go before they
 * switch anything off. Garmin reaches Atlas through Strava and Komoot only
 * arrives as GPX, hence no rows for either.
 */
export const KIND_SOURCE_LABELS: Record<ActivityKind, Partial<Record<KindLabelSource, string>>> = {
  hike: {
    strava: "Strava Hike and Snowshoe",
    apple_health: "Apple Health hiking",
    health_connect: "Health Connect hiking",
    gpx: "GPX typed as hiking",
  },
  walk: {
    strava: "Strava Walk",
    apple_health: "Apple Health walking",
    health_connect: "Health Connect walking",
    gpx: "GPX typed as walking",
  },
  run: {
    strava: "Strava Run and Trail run",
    apple_health: "Apple Health running",
    health_connect: "Health Connect running",
    gpx: "GPX typed as running",
  },
  other: {},
};

export const KIND_SOURCE_ORDER: readonly KindLabelSource[] = [
  "strava",
  "apple_health",
  "health_connect",
  "gpx",
];

/** "Strava Walk · Apple Health walking" for the connected sources. */
export function kindSourceSummary(kind: ActivityKind, connected: readonly string[]): string {
  const labels = KIND_SOURCE_LABELS[kind];
  return KIND_SOURCE_ORDER.filter((s) => s === "gpx" || connected.includes(s))
    .map((s) => labels[s])
    .filter((l): l is string => Boolean(l))
    .join(" · ");
}

// ─── Copy shared by web and mobile settings panels ───────────────────────────

export const IMPORT_PREFERENCES_COPY = {
  title: "What Atlas imports",
  intro:
    "Applies to every connected source from now on. Anything already on your record stays; remove it from Activities. Anything Atlas skips is kept under Removed activities so you can bring it back.",
  notMatched: "Skipped activities are not matched: a peak you reach on a run does not count while runs are off.",
  allowlist: "Rides, swims, gym and virtual activities never import. Untyped GPX uploads always import.",
  /** Shown when Walks is switched off. */
  walksOff:
    "Hike or walk is whatever your watch was set to. Apple Watch and many Garmin profiles record hikes as \"walk\", so turning walks off can skip real hikes.",
  deviceSync: "Open the Atlas app and sync to bring back Apple Health or Health Connect activities.",
} as const;

// ─── API shapes ──────────────────────────────────────────────────────────────

export interface ImportPreferencesResponse {
  excludedKinds: ActivityKind[];
  /** Count of skipped_by_preference tombstones per excludable kind. */
  skippedCounts: Record<"hike" | "walk" | "run", number>;
}

export interface RemovedActivity {
  /** activity_exclusions id (the tombstone). */
  id: string;
  sourceType: string;
  /** Null for GPX uploads. */
  sourceId: string | null;
  kind: ActivityKind;
  sportType: string | null;
  reason: ActivityExclusionReason;
  name: string;
  startedAt: string;
  distanceM: number | null;
  createdAt: string;
  /** True when Atlas parked the full row: restore is local and instant.
   * False for preference skips, which need the source asked again. */
  archived: boolean;
  /** reason = duplicate: the copy that was kept (null when it is gone too). */
  duplicateOf?: { id: string; name: string; sourceType: string } | null;
  /** reason = trip_route: the trip using it (restoring clears the trip's route). */
  tripRouteOf?: { id: string; name: string } | null;
}

/** One line per source × kind × reason for the Connected sources summary. */
export interface RemovedActivityGroup {
  sourceType: string;
  kind: ActivityKind;
  reason: ActivityExclusionReason;
  count: number;
  /** Most recent removal/skip in the group. */
  latestAt: string;
  /** How many of the group can be restored locally (archived). */
  archived: number;
}

export interface RemovedActivitiesResponse {
  items: RemovedActivity[];
  total: number;
  /** Unfiltered summary across all the user's removed/skipped activities. */
  groups: RemovedActivityGroup[];
}

export type BulkDeleteRequest =
  | { ids: string[] }
  | { filter: { kind?: ActivityKind; source?: string }; expectedCount: number };

export interface BulkDeleteResponse {
  deleted: number;
  tombstoned: number;
}

export type RestoreRemovedRequest =
  | { ids: string[] }
  /** Undo a remove by the activity ids that were removed (ATL-175). */
  | { activityIds: string[] }
  | {
      filter: { reason?: ActivityExclusionReason; kind?: ActivityKind; source?: string };
      expectedCount: number;
    };

export interface RestoreRemovedResponse {
  /** Tombstones cleared. */
  restored: number;
  /** Archived rows copied straight back and queued for re-matching. */
  restoredLocally: number;
  /** Strava re-fetches queued (preference skips only). */
  queued: number;
  /** Health-store preference skips that need the device to resend them. */
  needsDeviceSync: number;
}
